import React, {ChangeEvent} from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Link,
  useTheme,
  FormControl,
  FormLabel,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import {ViewFilled, ViewOff, CheckmarkFilled} from '@carbon/icons-react';
import PopUp from 'components/PopUp/PopUp';
import AutoCompleteSelector from 'components/AutoCompleteSelector/AutoCompleteSelector';
import languages from './Languages.json';
import countries from './Countries.json';
import {IExternalIDUser} from 'app/types/Shared';
import TurnstileWidget from 'components/TurnstileWidget/TurnstileWidget';

const CheckBoxIcon = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect width='20' height='20' fill='white' />
    <rect width='20' height='20' fill='white' />
  </svg>
);

const Checkmark = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M5 9.31034L8.31034 13.0345L15.3448 6'
      stroke='#450A0A'
      strokeWidth='3'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

type RegistrationProps = {
  title: string;
  open: boolean;
  email: string;
  confirm: (arg0: IExternalIDUser) => Promise<void>;
  cancel: () => void;
  pending?: boolean;
};
const RegistrationForm = ({title, open, email, confirm, cancel, pending = false}: RegistrationProps) => {
  const theme = useTheme();
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [country, setCountry] = React.useState('');
  const [language, setLanguage] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [viewPassword, setViewPassword] = React.useState(false);
  const [termsChecked, setTermsChecked] = React.useState(false);

  const clearInputs = () => {
    setFirstName('');
    setLastName('');
    setPassword('');
    setConfirmPassword('');
    setCountry('');
    setLanguage('');
    setViewPassword(false);
    setTermsChecked(false);
  };

  const passwordRequirements = (password: string) => {
    const lengthRegex = /^.{8,20}$/;
    const capitalLetterRegex = /(?=.*[A-Z])/;
    const lowercaseLetterRegex = /(?=.*[a-z])/;
    const numberRegex = /(?=.*\d)/;

    return (
      lengthRegex.test(password) &&
      capitalLetterRegex.test(password) &&
      lowercaseLetterRegex.test(password) &&
      numberRegex.test(password)
    );
  };
  const testPasswordLength = (password: string) => {
    const lengthRegex = /^.{8,20}$/;
    return lengthRegex.test(password);
  };

  const testPasswordCapitalLetter = (password: string) => {
    const capitalLetterRegex = /(?=.*[A-Z])/;
    return capitalLetterRegex.test(password);
  };

  const testPasswordLowercaseLetter = (password: string) => {
    const lowercaseLetterRegex = /(?=.*[a-z])/;
    return lowercaseLetterRegex.test(password);
  };

  const testPasswordNumber = (password: string) => {
    const numberRegex = /(?=.*\d)/;
    return numberRegex.test(password);
  };

  const handleFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFirstName(event.target.value);
  };

  const handleLastNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value);
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };
  const handleConfirmChange = (event: ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(event.target.value);
  };

  const registerUser = async () => {
    const countryCode = countries.find((countryObj) => countryObj.name === country)?.['alpha-2'];
    const languageCode = languages.find((languageObj) => languageObj.name === language)?.code;

    const newUser: IExternalIDUser = {
      email: email,
      firstName: firstName,
      lastName: lastName,
      country: countryCode ? countryCode : 'US',
      language: languageCode ? languageCode : 'en',
      password: password,
    };
    await confirm(newUser);
    clearInputs();
  };

  return (
    <PopUp
      title={title}
      onCancel={() => {
        cancel();
        clearInputs();
      }}
      onConfirm={registerUser}
      confirmText='Sign Up'
      open={open}
      confirmedDisabled={
        !firstName ||
        !lastName ||
        !password ||
        !confirmPassword ||
        !country ||
        !language ||
        !termsChecked ||
        password !== confirmPassword ||
        !passwordRequirements(password) ||
        pending
      }
      maxWidth='sm'
      width='520px'
    >
      <Box display='flex' justifyContent='center' alignItems='center' width='100%' height='100%'>
        {!pending ? (
          <Stack spacing='20px' width='100%' marginBottom='-20px'>
            <Stack spacing='10px' direction='row' width='100%'>
              <FormControl>
                <FormLabel sx={{marginBottom: '5px'}}>First Name</FormLabel>
                <TextField
                  variant='outlined'
                  size='small'
                  placeholder='Enter your First Name'
                  required
                  sx={{width: '235px', borderRadius: '6px'}}
                  value={firstName}
                  onChange={handleFirstNameChange}
                  //onSubmit={handleSubmit}
                />
              </FormControl>
              <FormControl>
                <FormLabel sx={{marginBottom: '5px'}}>Last Name</FormLabel>
                <TextField
                  variant='outlined'
                  size='small'
                  placeholder='Enter your Last Name'
                  required
                  sx={{width: '235px', borderRadius: '6px'}}
                  value={lastName}
                  onChange={handleLastNameChange}
                  //onSubmit={handleSubmit}
                />
              </FormControl>
            </Stack>

            <AutoCompleteSelector
              data={countries.map((country) => country.name)}
              placeholder='Select Country'
              title='Country'
              changeCallback={setCountry}
              defaultValue={country}
            />
            <AutoCompleteSelector
              data={languages.map((language) => language.name)}
              placeholder='Select preferred language'
              title='Preferred Language'
              changeCallback={setLanguage}
              defaultValue={language}
            />
            <FormControl>
              <FormLabel sx={{marginBottom: '5px'}}>Password</FormLabel>
              <TextField
                variant='outlined'
                size='small'
                placeholder='Create new Password'
                required
                sx={{
                  borderRadius: '6px',
                  'input::-ms-reveal': {
                    display: 'none',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='start'>
                      <IconButton onClick={() => setViewPassword(!viewPassword)} disableRipple sx={{p: 0}}>
                        {viewPassword ? <ViewFilled color={theme.palette.primary.main} /> : <ViewOff color='grey' />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                type={viewPassword ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
                //onSubmit={handleSubmit}
              />
              {password && !passwordRequirements(password) && (
                <Stack pl='14px' pt='5px'>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    {testPasswordLength(password) ? <CheckmarkFilled color='green' /> : <CheckmarkFilled color='red' />}
                    <Typography variant='h6'>Password must be 8-20 characters long</Typography>
                  </Stack>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    {testPasswordCapitalLetter(password) ? (
                      <CheckmarkFilled color='green' />
                    ) : (
                      <CheckmarkFilled color='red' />
                    )}
                    <Typography variant='h6'>Password must have a at least one capital letter (A-Z)</Typography>
                  </Stack>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    {testPasswordLowercaseLetter(password) ? (
                      <CheckmarkFilled color='green' />
                    ) : (
                      <CheckmarkFilled color='red' />
                    )}
                    <Typography variant='h6'>Password must have a at least one lowercase letter (a-z)</Typography>
                  </Stack>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    {testPasswordNumber(password) ? <CheckmarkFilled color='green' /> : <CheckmarkFilled color='red' />}
                    <Typography variant='h6'>Password must have at least one number (0-9)</Typography>
                  </Stack>
                </Stack>
              )}
            </FormControl>
            <FormControl>
              <FormLabel sx={{marginBottom: '5px'}}>Confirm Password</FormLabel>
              <TextField
                variant='outlined'
                size='small'
                placeholder='Re-enter new password'
                required
                sx={{
                  borderRadius: '6px',
                  'input::-ms-reveal': {
                    display: 'none',
                  },
                }}
                type={viewPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='start'>
                      <IconButton onClick={() => setViewPassword(!viewPassword)} disableRipple sx={{p: 0}}>
                        {viewPassword ? <ViewFilled color={theme.palette.primary.main} /> : <ViewOff color='grey' />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                value={confirmPassword}
                onChange={handleConfirmChange}
                //onSubmit={handleSubmit}
              />
              {password && confirmPassword && password !== confirmPassword && (
                <FormHelperText color='red'>Passwords do not match</FormHelperText>
              )}
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  onClick={() => setTermsChecked(!termsChecked)}
                  checked={termsChecked}
                  icon={<CheckBoxIcon />}
                  checkedIcon={
                    <Box
                      sx={{
                        bgcolor: 'white',
                        maxHeight: '20px',
                        maxWidth: '20px',
                        minHeight: '20px',
                        minWidth: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Checkmark />
                    </Box>
                  }
                />
              }
              label={
                <Typography variant='h5' fontSize='12px'>
                  I agree to the Takeda ID{' '}
                  <Link
                    color='primary'
                    underline='hover'
                    href='https://accounts.takeda.com/terms-and-conditions'
                    target='_blank'
                  >
                    Terms and Conditions
                  </Link>{' '}
                  and{' '}
                  <Link
                    underline='hover'
                    color='primary'
                    href='https://accounts.takeda.com/privacy-notice'
                    target='_blank'
                  >
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />
            <Box width='100%' display='flex' justifyContent='center' alignItems='center' minHeight='70px'>
              <TurnstileWidget />
            </Box>
          </Stack>
        ) : (
          <Stack px={10} py={10}>
            <CircularProgress />
          </Stack>
        )}
      </Box>
    </PopUp>
  );
};
export default RegistrationForm;
